import { io } from "socket.io-client";
import { backendUrl } from "../Router/constant";
import { AUTH_TOKEN_KEY } from "../actions/auth";

// The owner inbox rides the same `/chat` namespace as training and the widget.
// The handshake is the plain authenticated one — token, no mode — and the
// server checks that the org owns the assistant, exactly as it does for any
// other owner action. Ported from test-owner-inbox.html.
//
// One socket serves the whole inbox, not one per conversation: an owner can be
// joined to several conversations at once and each stays live independently.
// Joining pauses the AI for that conversation until it is left, replaced by
// another session, or auto-resumed after five idle minutes.

const socketOrigin = () => String(backendUrl || "").replace(/\/+$/, "");

/**
 * Opens the owner connection for an assistant.
 *
 * @param {string} assistantId
 * @param {object} handlers callbacks for the events the server emits
 * @returns {import("socket.io-client").Socket}
 */
export const connectOwner = (assistantId, handlers = {}) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);

  const socket = io(`${socketOrigin()}/chat`, {
    query: { assistantId },
    auth: { token },
    transports: ["websocket", "polling"],
  });

  socket.on("connect", () => {
    handlers.onConnect?.();
  });

  // The ack for our own join. `messages` carries the recent history, so the
  // thread can be rendered from the server's view rather than ours.
  socket.on("joined_live_conversation", (data) => {
    handlers.onJoined?.(data || {});
  });

  // Only one owner session may hold a conversation. Joining one that another
  // tab or device already holds forces that session out — and this is how it
  // finds out.
  socket.on("owner_session_replaced", (data) => {
    handlers.onSessionReplaced?.(data || {});
  });

  // Five minutes joined without sending anything and the backend hands the
  // conversation back to the AI on its own.
  socket.on("owner_auto_resumed", (data) => {
    handlers.onAutoResumed?.(data || {});
  });

  // Broadcast whenever ANY owner session joins or leaves — including other
  // browser tabs, which is why these are separate from the join ack.
  socket.on("owner_joined", (data) => {
    handlers.onOwnerJoined?.(data || {});
  });

  socket.on("owner_left", (data) => {
    handlers.onOwnerLeft?.(data || {});
  });

  socket.on("visitor_message", (data) => {
    handlers.onVisitorMessage?.(data || {});
  });

  // Our own replies come back through the room broadcast, so the thread is
  // rendered off this rather than an optimistic local append.
  socket.on("owner_message", (data) => {
    handlers.onOwnerMessage?.(data || {});
  });

  socket.on("error", (data) => {
    handlers.onError?.(data?.message || "Something went wrong.");
  });

  socket.on("connect_error", (err) => {
    handlers.onError?.(err?.message || "Connection rejected.", true);
  });

  socket.on("disconnect", () => {
    handlers.onDisconnect?.();
  });

  return socket;
};

/** Takes over a conversation. The AI stops answering it until we leave. */
export const joinLiveConversation = (socket, conversationId) => {
  if (!socket || !conversationId) return;

  const emit = () => socket.emit("join_live_conversation", { conversationId });

  // Clicking join before the handshake finishes is normal, so the emit waits
  // for the connection rather than being dropped.
  if (socket.connected) emit();
  else socket.once("connect", emit);
};

/** Hands one conversation back to the AI, leaving any others untouched. */
export const leaveLiveConversation = (socket, conversationId) => {
  if (!socket || !conversationId) return;
  socket.emit("leave_live_conversation", { conversationId });
};

/**
 * Which conversation is actually on screen. In-memory on the server and not
 * persisted, and it decides notifications: a message on a conversation that is
 * joined but not focused raises an in-app notification, one on the focused
 * conversation does not.
 *
 * @param {string} conversationId omit to say "not looking at anything"
 */
export const focusConversation = (socket, conversationId = "") => {
  if (!socket?.connected) return;
  socket.emit("focus_conversation", conversationId ? { conversationId } : {});
};

export const sendOwnerMessage = (socket, conversationId, message) => {
  if (!socket || !conversationId) return;
  socket.emit("owner_message", { conversationId, message });
};
